// Frontend-computed derived stats (Schema v3, Section 4)

interface Attributes {
  attr_str: number
  attr_sta: number
  attr_dex: number
  attr_agl: number
  attr_int: number
  attr_aur: number
  attr_wil: number
}

interface Injury {
  injury_levels: number
  healed_at: string | null
}

interface WeightedItem {
  weight_lbs: number
  quantity?: number
  location?: string
  is_equipped?: boolean
  is_worn?: boolean
  is_carried?: boolean
}

interface SkillSunsignMod {
  skill_name: string
  sunsign_id: string
  sb_bonus: number
}

interface Sunsign {
  sunsign_id: string
  sort_order: number
}

interface ArmourPiece {
  coverage: string[]
  material_id: string
  quality_mod: number
  is_worn: boolean
}

interface ArmourMaterial {
  material_id: string
  prot_blunt: number
  prot_edge: number
  prot_point: number
  prot_fire: number
}

export function computeEndurance(attrs: Attributes): number {
  return Math.round((attrs.attr_str + attrs.attr_sta + attrs.attr_wil) / 3)
}

export function computeSkillBase(
  sbAttr1: number,
  sbAttr2: number,
  sbAttr3: number,
  sunsignBonus: number
): number {
  return Math.round((sbAttr1 + sbAttr2 + sbAttr3) / 3) + sunsignBonus
}

export function sunsignBonus(
  skillName: string,
  sunsignId: string,
  onCusp: boolean,
  adjacentSignId: string | null,
  mods: SkillSunsignMod[]
): number {
  const primary = mods.find(m => m.skill_name === skillName && m.sunsign_id === sunsignId)
  const primaryBonus = primary?.sb_bonus ?? 0

  if (!onCusp || !adjacentSignId) return primaryBonus

  const adjacent = mods.find(m => m.skill_name === skillName && m.sunsign_id === adjacentSignId)
  const adjacentBonus = adjacent?.sb_bonus ?? 0

  return Math.max(primaryBonus, adjacentBonus)
}

export function adjacentSign(
  sunsignId: string,
  signs: Sunsign[]
): { prev: string | null; next: string | null } {
  const sorted = [...signs].sort((a, b) => a.sort_order - b.sort_order)
  const idx = sorted.findIndex(s => s.sunsign_id === sunsignId)
  if (idx === -1) return { prev: null, next: null }

  const prevIdx = idx === 0 ? sorted.length - 1 : idx - 1
  const nextIdx = idx === sorted.length - 1 ? 0 : idx + 1

  return {
    prev: sorted[prevIdx].sunsign_id,
    next: sorted[nextIdx].sunsign_id,
  }
}

export function computeUniversalPenalty(
  totalInjuryLevels: number,
  fatigueLevels: number
): number {
  return totalInjuryLevels + fatigueLevels
}

export function computeTotalInjuryLevels(injuries: Injury[]): number {
  return injuries
    .filter(i => i.healed_at === null)
    .reduce((sum, i) => sum + i.injury_levels, 0)
}

// Combat load: worn armour + equipped weapons + belt/in-hand items only.
// Backpack, saddlebag, and other carried containers are dropped before combat.
const COMBAT_LOCATIONS = new Set(['worn', 'belt', 'in_hand'])

export function computeLoad(
  armour: WeightedItem[],
  weapons: WeightedItem[],
  equipment: WeightedItem[]
): number {
  const armourLoad = armour
    .filter(a => a.is_worn)
    .reduce((sum, a) => sum + a.weight_lbs, 0)
  const weaponLoad = weapons
    .filter(w => w.is_equipped)
    .reduce((sum, w) => sum + w.weight_lbs, 0)
  const equipLoad = equipment
    .filter(e => e.is_carried && COMBAT_LOCATIONS.has(e.location ?? ''))
    .reduce((sum, e) => sum + e.weight_lbs * (e.quantity ?? 1), 0)

  return armourLoad + weaponLoad + equipLoad
}

export function computeEncumbrancePenalty(loadLbs: number, endurance: number): number {
  if (endurance <= 0) return 0
  return Math.round(loadLbs / endurance)
}

export function computePhysicalPenalty(
  universalPenalty: number,
  encumbrancePenalty: number
): number {
  return universalPenalty + encumbrancePenalty
}

export function computeEffectiveMove(agl: number, physicalPenalty: number): number {
  return Math.max(0, agl - physicalPenalty)
}

export function computeEffectiveDodge(dodgeML: number, universalPenalty: number): number {
  return Math.max(5, dodgeML - universalPenalty * 5)
}

// ─── Armour Skill EP pipeline (house rule — HM3_Armour_Skill_ClaudeCode_Spec.md) ───
//
// Tier materials (matches npc_equipment_generator.js):
//   harness: plate + (mail|ring|scale)
//   mail:    mail | ring | scale | plate-only
//   light:   leather | kurbul | quilt | cloth
//   none:    no armour
//
// EP pipeline:
//   rawEP = floor(armourWeight / max(1, END - fatigueLevels))
//   combatEP = max(0, round(rawEP × (1 − armourML/100)))
//   Climbing & Swimming always use rawEP (no reduction)

const MAIL_MATERIALS    = new Set(['Mail', 'Ring', 'Scale'])
const LIGHT_MATERIALS   = new Set(['Leather', 'Kurbul', 'Quilt', 'Cloth'])

interface WornArmourItem {
  material_id: string
  is_worn?: boolean
}

export type ArmourTier = 'none' | 'light' | 'mail' | 'harness'

export function getArmourTier(wornArmour: WornArmourItem[]): ArmourTier {
  const materials = new Set(wornArmour.map(a => a.material_id).filter(Boolean))
  const hasPlate = materials.has('Plate')
  const hasMail  = [...MAIL_MATERIALS].some(m => materials.has(m))
  if (hasPlate && hasMail) return 'harness'
  if (hasPlate || hasMail) return 'mail'
  if ([...LIGHT_MATERIALS].some(m => materials.has(m))) return 'light'
  return 'none'
}

export function calcRawEP(armourWeight: number, end: number, fatigueLevels: number): number {
  return Math.floor(armourWeight / Math.max(1, end - fatigueLevels))
}

export function calcCombatEP(rawEP: number, armourML: number): number {
  return Math.max(0, Math.round(rawEP * (1 - armourML / 100)))
}

export function calcEffectiveEP(
  armourWeight: number,
  end: number,
  fatigueLevels: number,
  armourML: number,
  isClimbOrSwim: boolean
): number {
  const rawEP = calcRawEP(armourWeight, end, fatigueLevels)
  if (isClimbOrSwim) return rawEP
  return calcCombatEP(rawEP, armourML)
}

// Armour Layering Engine — Option D House Rule (Schema v3, Section 5)
//
// Step 1: Same-material penalty — 1st ×1.0, 2nd ×0.6, 3rd+ ×0.3, floor each
// Step 2: Aspect caps — B×2.0, E×2.0, P×1.5, F×2.0 of best single layer, floor
// Ring/Scale include Leather base — Leather filtered when Ring/Scale present

export interface LocationProtection {
  blunt: number
  edge: number
  point: number
  fire: number
}

const SAME_MATERIAL_MULTS = [1.0, 0.6, 0.3]
const ASPECT_CAP_MULTS = { blunt: 2.0, edge: 2.0, point: 1.5, fire: 2.0 } as const
const ASPECTS = ['blunt', 'edge', 'point', 'fire'] as const
const ASPECT_KEYS: Record<string, 'prot_blunt' | 'prot_edge' | 'prot_point' | 'prot_fire'> = {
  blunt: 'prot_blunt', edge: 'prot_edge', point: 'prot_point', fire: 'prot_fire',
}

export function computeArmourByLocation(
  wornArmour: ArmourPiece[],
  materials: ArmourMaterial[]
): Record<string, LocationProtection> {
  const materialMap = new Map(materials.map(m => [m.material_id, m]))

  // Build per-location layer lists: location → [{material_id, quality_mod}]
  const locationLayers: Record<string, Array<{ material_id: string; quality_mod: number }>> = {}

  for (const piece of wornArmour) {
    if (!piece.is_worn) continue
    if (!materialMap.has(piece.material_id)) continue

    for (const location of piece.coverage) {
      if (!locationLayers[location]) locationLayers[location] = []
      locationLayers[location].push({ material_id: piece.material_id, quality_mod: piece.quality_mod })
    }
  }

  const result: Record<string, LocationProtection> = {}

  for (const [location, layers] of Object.entries(locationLayers)) {
    // Filter Leather when Ring or Scale present at this location
    const hasRingOrScale = layers.some(l => l.material_id === 'Ring' || l.material_id === 'Scale')
    const effective = hasRingOrScale ? layers.filter(l => l.material_id !== 'Leather') : layers
    if (effective.length === 0) continue

    // Step 1 — Same-material penalty
    const materialCount: Record<string, number> = {}
    const sum = { blunt: 0, edge: 0, point: 0, fire: 0 }
    const best = { blunt: 0, edge: 0, point: 0, fire: 0 }

    for (const layer of effective) {
      const mat = materialMap.get(layer.material_id)!
      const count = materialCount[layer.material_id] ?? 0
      const mult = SAME_MATERIAL_MULTS[Math.min(count, SAME_MATERIAL_MULTS.length - 1)]

      for (const aspect of ASPECTS) {
        const baseVal = mat[ASPECT_KEYS[aspect]] + layer.quality_mod
        sum[aspect] += Math.floor(baseVal * mult)
        if (baseVal > best[aspect]) best[aspect] = baseVal
      }

      materialCount[layer.material_id] = count + 1
    }

    // Step 2 — Aspect caps
    const prot = { blunt: 0, edge: 0, point: 0, fire: 0 }
    for (const aspect of ASPECTS) {
      const cap = Math.floor(best[aspect] * ASPECT_CAP_MULTS[aspect])
      prot[aspect] = Math.min(sum[aspect], cap)
    }

    result[location] = prot
  }

  return result
}
